import React from "react";
import { Client } from "@/types";
import { EditIcon } from "@/components/icons";

interface ClientProfileSectionProps {
  client: Client;
  onEdit?: () => void;
}

const LOCALE_LABELS: Record<string, string> = {
  en: "English",
  it: "Italian",
  ro: "Romanian",
  de: "German",
  fr: "French",
  es: "Spanish",
  pt: "Portuguese",
};

export const ClientProfileSection: React.FC<ClientProfileSectionProps> = ({
  client,
  onEdit,
}) => {
  const localeLabel = client.locale
    ? LOCALE_LABELS[client.locale] || client.locale
    : "Auto-detect";

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-gray-900">
          Client Profile
        </h3>
        {onEdit && (
          <button
            onClick={onEdit}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200 transition-colors"
          >
            <EditIcon className="w-3.5 h-3.5" />
            Edit
          </button>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 text-sm">
        <div>
          <span className="text-gray-500">Name</span>
          <p className="font-medium text-gray-900">{client.clientName}</p>
        </div>

        <div>
          <span className="text-gray-500">Status</span>
          <p className="font-medium text-gray-900">
            {client.status === "ARCHIVED" ? "Archived" : "Active"}
          </p>
        </div>

        <div>
          <span className="text-gray-500">Language</span>
          <p className="font-medium text-gray-900">{localeLabel}</p>
        </div>

        <div>
          <span className="text-gray-500">Sector</span>
          <p className="font-medium text-gray-900">
            {client.sector || "Not set"}
          </p>
        </div>

        <div>
          <span className="text-gray-500">Relationship Started</span>
          <p className="font-medium text-gray-900">
            {client.relationshipStartedAt
              ? new Date(client.relationshipStartedAt).toLocaleDateString(
                  "en-US",
                  { year: "numeric", month: "short", day: "numeric" }
                )
              : "Not set"}
          </p>
        </div>

        <div>
          <span className="text-gray-500">Followers (Start)</span>
          <p className="font-medium text-gray-900">
            {client.instagramStartFollowers
              ? `IG: ${client.instagramStartFollowers.toLocaleString()}`
              : ""}
            {client.instagramStartFollowers &&
            client.facebookStartFollowers
              ? " / "
              : ""}
            {client.facebookStartFollowers
              ? `FB: ${client.facebookStartFollowers.toLocaleString()}`
              : ""}
            {!client.instagramStartFollowers &&
            !client.facebookStartFollowers
              ? "Not set"
              : ""}
          </p>
        </div>

        <div>
          <span className="text-gray-500">Website</span>
          <p className="font-medium text-gray-900">
            {client.websiteUrl ? (
              <a
                href={client.websiteUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:text-blue-800 hover:underline"
              >
                {client.websiteUrl}
              </a>
            ) : (
              "Not set"
            )}
          </p>
        </div>

        {client.links && client.links.length > 0 && (
          <div className="sm:col-span-2">
            <span className="text-gray-500">Links</span>
            <div className="flex flex-wrap gap-2 mt-1">
              {client.links.map((link, idx) => (
                <a
                  key={idx}
                  href={link.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-800 hover:underline"
                >
                  {link.name}
                </a>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
